/**
 * Kleine, frameworklose UI-Bausteine: Buttons, Felder, Modal, Toast,
 * Confirm-Dialog, Spinner, Empty-State, Badge.
 */
import { h, clear, type Child } from "@/lib/dom";
import { icon, type IconName } from "@/lib/icons";
import { NAMED_COLORS, isPaletteColor, defaultColor } from "@/lib/colors";
import { subjectIcon, searchSubjectIcons, subjectIconNames } from "@/lib/subjectIcons";

/* ---------- Buttons ---------- */

export interface ButtonOptions {
  label?: string;
  icon?: IconName;
  variant?: "default" | "primary" | "ghost" | "danger";
  size?: "sm" | "md";
  type?: "button" | "submit";
  block?: boolean;
  disabled?: boolean;
  title?: string;
  onClick?: (e: MouseEvent) => void;
}

export function button(opts: ButtonOptions): HTMLButtonElement {
  const classes = ["btn"];
  if (opts.variant && opts.variant !== "default") classes.push(`btn-${opts.variant}`);
  if (opts.size === "sm") classes.push("btn-sm");
  if (opts.block) classes.push("btn-block");
  if (!opts.label && opts.icon) classes.push("btn-icon");

  return h(
    "button",
    {
      class: classes.join(" "),
      type: opts.type ?? "button",
      disabled: opts.disabled,
      title: opts.title,
      onclick: opts.onClick as EventListener | undefined,
    },
    opts.icon
      ? h("span", {
          class: "row",
          html: icon(opts.icon, opts.size === "sm" ? 16 : 18),
        })
      : null,
    opts.label ?? null
  );
}

/**
 * Stift-Knopf für den Bearbeitungsmodus einer Seite. Der Zustand gehört der
 * jeweiligen Seite selbst (nicht global/persistiert) – die Seite hält ihn in
 * einer lokalen Variable und rendert bei `onToggle` neu.
 */
export function editModeButton(opts: { active: boolean; onToggle: () => void }): HTMLButtonElement {
  const btn = button({
    icon: "pencil",
    variant: "ghost",
    title: opts.active ? "Bearbeiten beenden" : "Bearbeiten",
    onClick: opts.onToggle,
  });
  btn.classList.add("edit-btn");
  btn.classList.toggle("active", opts.active);
  return btn;
}

/* ---------- Formularfelder ---------- */

export function field(
  label: string,
  control: HTMLElement,
  opts: { hint?: string; error?: string; id?: string } = {}
): HTMLElement {
  return h(
    "div",
    { class: "field" },
    h("label", opts.id ? { htmlFor: opts.id } : null, label),
    control,
    opts.hint ? h("div", { class: "field-hint" }, opts.hint) : null,
    opts.error ? h("div", { class: "field-error" }, opts.error) : null
  );
}

export function textInput(opts: {
  value?: string;
  type?: string;
  placeholder?: string;
  id?: string;
  name?: string;
  required?: boolean;
  autocomplete?: string;
  oninput?: (e: Event) => void;
}): HTMLInputElement {
  return h("input", {
    class: "input",
    type: opts.type ?? "text",
    value: opts.value ?? "",
    placeholder: opts.placeholder ?? "",
    id: opts.id,
    name: opts.name,
    required: opts.required,
    autocomplete: opts.autocomplete,
    oninput: opts.oninput as EventListener | undefined,
  });
}

export function textArea(opts: {
  value?: string;
  placeholder?: string;
  id?: string;
  rows?: number;
}): HTMLTextAreaElement {
  return h("textarea", {
    class: "textarea",
    placeholder: opts.placeholder ?? "",
    id: opts.id,
    rows: opts.rows ?? 3,
    value: opts.value ?? "",
  });
}

export interface SelectOption {
  value: string;
  label: string;
}

export function select(opts: {
  options?: SelectOption[];
  /** Alternativ nach Überschriften gruppiert (<optgroup>). */
  groups?: { label: string; options: SelectOption[] }[];
  value?: string;
  id?: string;
  onchange?: (e: Event) => void;
}): HTMLSelectElement {
  const el = h("select", {
    class: "select",
    id: opts.id,
    onchange: opts.onchange as EventListener | undefined,
  });
  // Nur der ERSTE Treffer wird ausgewählt. Steht derselbe Wert in mehreren
  // Gruppen (z. B. das Fach unter „Heute" und noch einmal unter „Alle"),
  // gewinnt sonst der letzte – und die Auswahl stünde unten statt oben.
  let matched = false;
  const option = (o: SelectOption) => {
    const opt = h("option", { value: o.value }, o.label);
    if (!matched && o.value === opts.value) {
      opt.selected = true;
      matched = true;
    }
    return opt;
  };
  for (const o of opts.options ?? []) el.appendChild(option(o));
  for (const g of opts.groups ?? []) {
    if (!g.options.length) continue;
    el.appendChild(h("optgroup", { label: g.label }, ...g.options.map(option)));
  }
  return el;
}

/* ---------- Status / Platzhalter ---------- */

export function spinner(): HTMLElement {
  return h("div", { class: "spinner" });
}

export function loading(): HTMLElement {
  return h("div", { class: "loading" }, spinner());
}

/**
 * Vollflächiger, blockierender Ladescreen: graut die Seite aus, fängt alle
 * Klicks ab und zeigt den Spinner mittig. Gibt eine Funktion zum Entfernen
 * zurück. Für Aktionen, bei denen der Nutzer warten muss (z. B. Benotung).
 */
export function blockingOverlay(message = "Wird verarbeitet …"): () => void {
  const overlay = h(
    "div",
    { class: "blocking-overlay" },
    h("div", { class: "blocking-overlay-box" }, spinner(), h("div", { class: "blocking-overlay-msg" }, message))
  );
  // Klicks/Tasten schlucken, solange der Overlay aktiv ist
  overlay.addEventListener("click", (e) => e.stopPropagation());
  document.body.appendChild(overlay);
  document.body.style.overflow = "hidden";
  return () => {
    overlay.remove();
    document.body.style.overflow = "";
  };
}

export function emptyState(opts: {
  icon: IconName;
  title: string;
  text?: string;
  action?: HTMLElement;
}): HTMLElement {
  return h(
    "div",
    { class: "empty" },
    h("div", { class: "empty-icon", html: icon(opts.icon, 40) }),
    h("h3", null, opts.title),
    opts.text ? h("p", null, opts.text) : null,
    opts.action ? h("div", { class: "empty-action" }, opts.action) : null
  );
}

export function badge(text: string, kind = ""): HTMLElement {
  return h("span", { class: `badge${kind ? " badge-" + kind : ""}` }, text);
}

/**
 * Farbauswahl: eine flache Reihe aus eigenständig benannten Farben (kein
 * Gruppieren nach Tonwert). Eine bestehende Farbe außerhalb der Palette
 * (z. B. aus einer älteren Version) bleibt zusätzlich wählbar.
 */
export function swatchPicker(initial: string): {
  el: HTMLElement;
  getValue: () => string;
} {
  let value = (initial || defaultColor()).toLowerCase();
  const buttons: HTMLButtonElement[] = [];

  const swatch = (hex: string, name: string) => {
    const b = h("button", {
      type: "button",
      class: "swatch" + (hex.toLowerCase() === value ? " active" : ""),
      style: `background:${hex}`,
      title: name,
      "aria-label": name,
      html: icon("check", 13),
      onclick: () => {
        value = hex.toLowerCase();
        buttons.forEach((x) => x.classList.remove("active"));
        b.classList.add("active");
      },
    });
    buttons.push(b);
    return b;
  };

  const swatches = NAMED_COLORS.map((c) => swatch(c.hex, c.name));
  // Bestehende Farbe außerhalb der Palette bleibt wählbar (sonst ginge sie
  // beim ersten Speichern verloren).
  if (value && !isPaletteColor(value)) {
    swatches.unshift(swatch(value, "Aktuelle Farbe"));
  }

  return { el: h("div", { class: "swatches" }, ...swatches), getValue: () => value };
}

/**
 * Icon-Auswahl für Fächer: kein eigenes Suchfeld – Vorschläge kommen aus dem
 * Fachnamen (siehe `setQuery`). Treffer erscheinen oben als „Empfohlen",
 * die übrigen Icons darunter.
 */
export function iconPicker(initial: string): {
  el: HTMLElement;
  getValue: () => string;
  setQuery: (q: string) => void;
} {
  let value = initial || "book";
  let query = "";
  const body = h("div", { class: "icon-picker-body" });

  function iconButton(name: string): HTMLElement {
    return h("button", {
      type: "button",
      class: "icon-opt" + (name === value ? " active" : ""),
      title: name,
      html: subjectIcon(name, 22),
      onclick: () => {
        value = name;
        render();
      },
    });
  }

  function render() {
    clear(body);
    const q = query.trim();
    const matched = q ? searchSubjectIcons(q) : [];
    if (matched.length) {
      const matchedSet = new Set(matched);
      const rest = subjectIconNames().filter((n) => !matchedSet.has(n));
      body.appendChild(h("div", { class: "icon-group-label" }, "Empfohlen"));
      body.appendChild(h("div", { class: "icon-grid" }, ...matched.map(iconButton)));
      if (rest.length) {
        body.appendChild(h("div", { class: "icon-group-label" }, "Weitere Icons"));
        body.appendChild(h("div", { class: "icon-grid" }, ...rest.map(iconButton)));
      }
    } else {
      body.appendChild(h("div", { class: "icon-grid" }, ...subjectIconNames().map(iconButton)));
    }
  }
  render();

  return {
    el: h("div", { class: "icon-picker" }, body),
    getValue: () => value,
    setQuery: (q: string) => {
      query = q;
      render();
    },
  };
}

/* ---------- Modal ---------- */

export interface ModalHandle {
  close: () => void;
}

export function openModal(opts: {
  title: string;
  body: Child;
  footer?: Child;
  onClose?: () => void;
  wide?: boolean;
  /**
   * Erzwingt die zentrierte Desktop-Darstellung auch unter 560px (Standard
   * dort: Bottom-Sheet). Für Dialoge mit einem Textfeld, das sofort die
   * Tastatur öffnet – sonst startet das Sheet unten und rutscht beim
   * Aufklappen der Tastatur noch einmal sichtbar nach oben.
   */
  centered?: boolean;
  /**
   * Warnt (Wackeln) beim Schließen mit ungespeicherten Eingaben. Für Dialoge,
   * die sofort speichern (z. B. Einstellungen), auf `false` setzen.
   */
  guardUnsaved?: boolean;
  /**
   * Escape schließt den Dialog (Standard). Aus für Dialoge, in denen Escape
   * selbst gebraucht wird (Tabellen-Editor: Zelleingabe abbrechen) – dort
   * wäre sonst die ganze Arbeit weg.
   */
  closeOnEscape?: boolean;
  /** Enter in einem Textfeld löst „Speichern" aus (Standard). Aus, wo Enter selbst gebraucht wird. */
  submitOnEnter?: boolean;
}): ModalHandle {
  const backdrop = h("div", { class: "modal-backdrop" + (opts.centered ? " modal-backdrop-centered" : "") });
  const guard = opts.guardUnsaved !== false;
  let dirty = false;

  const close = () => {
    backdrop.remove();
    document.removeEventListener("keydown", onKey);
    opts.onClose?.();
  };
  const shake = () => {
    modal.classList.remove("shake");
    void modal.offsetWidth;
    modal.classList.add("shake");
  };
  // Außenklick / Escape: schließt bei sauberem Stand, wackelt sanft bei
  // ungespeicherten Änderungen (X-Button und Abbrechen schließen immer).
  const tryClose = () => {
    if (guard && dirty) shake();
    else close();
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Escape" && opts.closeOnEscape !== false) tryClose();
  };

  const modal = h(
    "div",
    { class: "modal" + (opts.wide ? " modal-wide" : "") },
    h(
      "div",
      { class: "modal-header" },
      h("h2", null, opts.title),
      button({ icon: "x", variant: "ghost", size: "sm", title: "Schließen", onClick: close })
    ),
    h("div", { class: "modal-body" }, opts.body),
    opts.footer ? h("div", { class: "modal-footer" }, opts.footer) : null
  );

  modal.addEventListener("input", () => {
    dirty = true;
  });
  modal.addEventListener("change", () => {
    dirty = true;
  });

  // Enter in einem einzeiligen Textfeld löst die Primär-Aktion im Footer aus
  // (Speichern/OK). Textareas behalten Enter als Zeilenumbruch.
  modal.addEventListener("keydown", (e: KeyboardEvent) => {
    if (e.key !== "Enter" || e.isComposing || e.defaultPrevented || opts.submitOnEnter === false) return;
    const t = e.target as HTMLElement;
    if (!(t instanceof HTMLInputElement)) return;
    if (t.type === "checkbox" || t.type === "radio" || t.type === "file") return;
    const primary = modal.querySelector<HTMLButtonElement>(".modal-footer .btn-primary");
    if (primary && !primary.disabled) {
      e.preventDefault();
      primary.click();
    }
  });

  backdrop.appendChild(modal);
  backdrop.addEventListener("click", (e) => {
    if (e.target === backdrop) tryClose();
  });
  document.addEventListener("keydown", onKey);
  document.body.appendChild(backdrop);

  return { close };
}

export function confirmDialog(opts: {
  title: string;
  message: string;
  confirmLabel?: string;
  danger?: boolean;
}): Promise<boolean> {
  return new Promise((resolve) => {
    let settled = false;
    const done = (val: boolean) => {
      if (settled) return;
      settled = true;
      handle.close();
      resolve(val);
    };
    const footer = h(
      "div",
      { class: "row" },
      button({ label: "Abbrechen", variant: "ghost", onClick: () => done(false) }),
      button({
        label: opts.confirmLabel ?? "OK",
        variant: opts.danger ? "danger" : "primary",
        onClick: () => done(true),
      })
    );
    const handle = openModal({
      title: opts.title,
      body: h("p", { class: "muted" }, opts.message),
      footer,
      onClose: () => done(false),
    });
  });
}

/* ---------- Toast ---------- */

let toastContainer: HTMLElement | null = null;

const TOAST_ICON: Record<"info" | "error" | "success" | "warning", IconName> = {
  info: "lightbulb",
  error: "x",
  success: "check",
  warning: "alert-triangle",
};

export function toast(
  message: string,
  kind: "info" | "error" | "success" | "warning" = "info",
  ms = 3200
): void {
  if (!toastContainer) {
    toastContainer = h("div", { class: "toast-container" });
    document.body.appendChild(toastContainer);
  }

  let removed = false;
  const remove = () => {
    if (removed) return;
    removed = true;
    t.classList.add("toast-out");
    t.addEventListener("animationend", () => {
      t.remove();
      if (toastContainer && toastContainer.childElementCount === 0) {
        toastContainer.remove();
        toastContainer = null;
      }
    }, { once: true });
  };

  const t = h(
    "div",
    {
      class: `toast toast-${kind}`,
      role: kind === "error" ? "alert" : "status",
    },
    h("span", { class: "toast-icon", html: icon(TOAST_ICON[kind], 15) }),
    h("span", { class: "toast-message" }, message),
    h("button", {
      class: "toast-close",
      type: "button",
      title: "Schließen",
      "aria-label": "Schließen",
      html: icon("x", 13),
      onclick: (e: MouseEvent) => {
        e.stopPropagation();
        remove();
      },
    })
  );
  toastContainer.appendChild(t);

  // Klick auf die Fläche schließt ebenfalls sofort
  t.addEventListener("click", remove);
  setTimeout(remove, ms);
}
